import React, { useState, useEffect } from 'react';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Legend, ResponsiveContainer, Tooltip
} from 'recharts';
import {
  Box,
  Typography,
  Select,
  MenuItem,
  CircularProgress,
  useMediaQuery,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import emptyscreen from "../../../../assets/Dashboard/emptyscreen.svg";
import graphicon from "../../../../assets/Dashboard/graphicon.svg";
import CustomText, { textStyles } from '../../../../components/CustomText';
import { BASE_URL } from "../../../../components/BaseUrl";
import { decryptToken } from "../../../../Utlis/Encryption";
import { showUnderDevelopment } from "../../../../components/alerts";

const MONTHS = [
  "Jan","Feb","Mar","Apr","May","Jun",
  "Jul","Aug","Sep","Oct","Nov","Dec"
];

const PerformanceChart = () => {
  const isMobile = useMediaQuery("(max-width:768px)");
  const { t } = useTranslation();

  const [year, setYear] = useState(new Date().getFullYear());
  const [chartData, setChartData] = useState([]);
  const [loading, setLoading] = useState(false);

  const rawEncryptedToken = localStorage.getItem("token");
  const token = rawEncryptedToken ? decryptToken(rawEncryptedToken) : null;

  useEffect(() => {
    const controller = new AbortController();

    async function loadData() {
      try {
        setLoading(true);
        setChartData([]);
        const res = await fetch(
          `${BASE_URL}/account-executive/dashboard/performance-metrics1?year=${year}`,
          {
            method: "GET",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${token}`,
            },
            signal: controller.signal,
          }
        );
        if (!res.ok) throw new Error("Failed to fetch");
        const json = await res.json();

        if (!json.error && json.data) {
          const earnings = json.data.monthly_breakdown.earnings;
          const counts = json.data.monthly_breakdown.earning_counts;

          const data = MONTHS.map((label, idx) => {
            const monthKey = Object.keys(earnings)[idx];
            return {
              name: label,
              Earning: earnings[monthKey],
              CompanyClients: counts[monthKey],
              Inactive: 0, // adjust when backend provides inactive data
            };
          });

          const hasData = data.some(
            (d) => d.Earning > 0 || d.CompanyClients > 0 || d.Inactive > 0
          );

          setChartData(hasData ? data : []);
        }
      } catch (err) {
        console.error(err);
        setChartData([]);
      } finally {
        setLoading(false);
      }
    }

    loadData();
    return () => controller.abort();
  }, [year]);

  const labelMap = {
    Earning: t('performanceChart.earning'),
    CompanyClients: t('performanceChart.companyClients'),
    Inactive: t('performanceChart.inactive'),
  };

  // Custom Tooltip to show translated labels on hover
  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      return (
        <Box
          sx={{
            backgroundColor: '#fff',
            border: '1px solid #ccc',
            borderRadius: '4px',
            padding: '8px 12px',
            boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
          }}
        >
          <Typography sx={{ fontSize: '12px', fontWeight: 600, mb: 0.5 }}>
            {label}
          </Typography>
          {payload.map((entry, index) => (
            <Typography
              key={index}
              sx={{
                fontSize: '12px',
                color: entry.color,
                fontWeight: 500,
              }}
            >
              {labelMap[entry.dataKey] || entry.dataKey}: {entry.value}
            </Typography>
          ))}
        </Box>
      );
    }
    return null;
  };

  // Generate years dropdown from 2015 → current year
  const years = Array.from({ length: 100 }, (_, i) => 2015 + i);
  const currentYear = new Date().getFullYear();

  return (
    <Box
      sx={{
        p: 2,
        backgroundColor: "white",
        border: "1px solid #091E4224",
        borderRadius: 2,
        boxShadow: "0px 0px 10px rgba(0,0,0,0.05)",
      }}
    >
      {/* Header */}
      <Box
        sx={{
          display: "flex",
          flexWrap: "wrap",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 2,
        }}
      >
        <Typography
          sx={{
            fontSize: 15,
            fontFamily: "Nunito Sans, sans-serif",
            fontWeight: 700,
            color: "#212121",
          }}
        >
          {t("performanceChart.title")}
        </Typography>

        <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
          <Select
            size="small"
            value={year}
            onChange={(e) => setYear(e.target.value)}
            sx={{
              width: 90,
              fontSize: "13px",
              height: 32,
              "& .MuiSelect-select": {
                padding: "4px 8px",
                fontWeight: 600,
                textAlign: "center",
              },
            }}
            MenuProps={{
              PaperProps: {
                sx: {
                  maxHeight: 200,
                  "& .MuiMenuItem-root": {
                    fontSize: "13px",
                    padding: "6px 10px",
                  },
                },
              },
            }}
          >
            {years.map((yr) => (
              <MenuItem
                key={yr}
                value={yr}
                disabled={yr > currentYear}
              >
                {yr}
              </MenuItem>
            ))}
          </Select>

       
        </Box>
      </Box>

      {/* Content: loader / empty / chart */}
      <Box sx={{ minHeight: 300, display: "flex", justifyContent: "center", alignItems: "center" }}>
        {loading ? (
          <CircularProgress size={30} />
        ) : chartData.length === 0 ? (
          <Box sx={{ display: "flex", flexDirection: "column", justifyContent: "center", alignItems: "center", height: 300 }}>
            <img src={emptyscreen} alt="No Data" style={{ width: 200 }} />
            <CustomText
              sx={{
                ...textStyles.h1,
                fontWeight: 500,
                fontSize: "16px",
                color: "#003149",
                pt: 1,
              }}
            >
              {t("no_data_yet")}
            </CustomText>
          </Box>
        ) : (
      
          <ResponsiveContainer width="100%" height={300}>
  <BarChart data={chartData} barCategoryGap="30%" barGap={0}>
    <CartesianGrid stroke="#f5f5f5" vertical={false} />

    <XAxis dataKey="name" tick={{ fontSize: 12 }} />

    {/* Left Y-axis for Earning */}
    <YAxis
      yAxisId="left"
      tick={{ fontSize: 12, fill: "#030229" }}
    />

    {/* Right Y-axis for CompanyClients */}
    <YAxis
      yAxisId="right"
      orientation="right"
      tick={{ fontSize: 12, fill: "#030229" }}
    />

    <Tooltip content={<CustomTooltip />} />
    <Legend formatter={(value) => labelMap[value] || value} />

    {/* Bars */}
    <Bar dataKey="Earning" yAxisId="left" fill="#2AC769" barSize={8} radius={[6, 6, 0, 0]} />
    <Bar dataKey="CompanyClients" yAxisId="right" fill="#3B82F6" barSize={8} radius={[6, 6, 0, 0]} />
    <Bar dataKey="Inactive" yAxisId="right" fill="#F43F5E" barSize={8} radius={[6, 6, 0, 0]} />
  </BarChart>
</ResponsiveContainer>
        )}
      </Box>
    </Box>
  );
};

export default PerformanceChart;


